import { useEffect, useRef, useState } from "react";
import { confirm as coreConfirm, modal as coreModal } from "../components/Modal/Modal";
import { Button } from "@humansignal/ui";

/**
 * Promise-based modal helpers that replace the browser's synchronous
 * ``window.confirm`` / ``window.prompt``.
 *
 * Usage:
 *   const ok = await askConfirm({ title: "Delete?", body: "...", danger: true });
 *   if (!ok) return;
 *
 *   const name = await askText({ title: "Rename", defaultValue: current });
 *   if (name === null) return; // user cancelled
 *
 * Both resolve to ``null`` / ``false`` on cancel or Esc, so callers can
 * handle the cancel case without try/catch.
 */

export const askConfirm = ({ title, body, okText = "Continue", cancelText = "Cancel", danger = false } = {}) =>
  new Promise((resolve) => {
    let done = false;
    const settle = (value) => {
      if (done) return;
      done = true;
      resolve(value);
    };
    coreConfirm({
      title,
      body,
      okText,
      cancelText,
      buttonLook: danger ? "negative" : "primary",
      onOk: () => settle(true),
      onCancel: () => settle(false),
      onHidden: () => settle(false), // user closed via backdrop / esc
    });
  });

/** Inner form rendered inside the ask-text modal. */
const AskTextForm = ({ defaultValue, placeholder, description, okText, cancelText, onSubmit, onCancel }) => {
  const [value, setValue] = useState(defaultValue ?? "");
  const inputRef = useRef(null);
  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select?.();
  }, []);
  const submit = (e) => {
    e?.preventDefault?.();
    onSubmit(value);
  };
  return (
    <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 360 }}>
      {description && (
        <p
          style={{ margin: 0, fontSize: "var(--font-size-body-small)", color: "var(--color-neutral-content-subtler)" }}
        >
          {description}
        </p>
      )}
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholder ?? ""}
        style={{
          height: 36,
          padding: "0 12px",
          border: "1px solid var(--color-neutral-border)",
          borderRadius: 6,
          background: "var(--color-neutral-surface)",
          color: "var(--color-neutral-content)",
          fontSize: "var(--font-size-body-small)",
        }}
      />
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 4 }}>
        <Button type="button" variant="negative" look="outlined" onClick={onCancel}>
          {cancelText}
        </Button>
        <Button type="submit" disabled={!value.trim()}>
          {okText}
        </Button>
      </div>
    </form>
  );
};

export const askText = ({
  title = "Enter a value",
  description,
  defaultValue = "",
  placeholder = "",
  okText = "Save",
  cancelText = "Cancel",
} = {}) =>
  new Promise((resolve) => {
    let done = false;
    let handle = null;
    const settle = (value) => {
      if (done) return;
      done = true;
      resolve(value);
      try {
        handle?.close?.();
      } catch {
        /* noop */
      }
    };
    handle = coreModal({
      title,
      body: (
        <AskTextForm
          defaultValue={defaultValue}
          placeholder={placeholder}
          description={description}
          okText={okText}
          cancelText={cancelText}
          onSubmit={(v) => settle(v?.trim() ? v.trim() : null)}
          onCancel={() => settle(null)}
        />
      ),
      onHidden: () => settle(null),
      simple: false,
      style: { width: 440 },
    });
  });
